import { StackScreenProps } from "@react-navigation/stack";
import { ActionsRootParamList } from "../../../../types";
import {
  AccountFeatureFlag,
  DataCoreBaseProduct,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { ResourcePageSkeleton } from "../ResourcPageSkeleton";
import { PlaceholderLoadingScreen } from "../../../products/screens/PlaceholderLoadingScreen";
import { useQueryFindBaseProductByBarcodeAsync } from "../../../../utilities/retailer-data-core";
import {
  AddCartNonMatchingProductForm,
  AddCartNonMatchingProductFormValues,
} from "../../components/AddCartNonMatchingProductForm";
import {
  Button,
  Page,
  Stack,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useActiveLocation } from "../../../../utilities/app-state/hooks";
import Location from "../../../../utilities/data/model/Location";
import { Colors } from "react-native-ui-lib";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDatabase } from "@nozbe/watermelondb/react";
import { useAddItem } from "../../utils/sale-controller";
import Product from "../../../../utilities/data/model/Product";
import { Q } from "@nozbe/watermelondb";
import { NonMatchingProductBanner } from "../../../../components/NonMatchingProductBanner";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";

export type AddNonProductModalScreenProps = StackScreenProps<
  ActionsRootParamList,
  "AddNonMatchedProductScreen"
>;

const AddNonMatchedProductContent = ({
  baseProduct,
  navigation,
}: AddNonProductModalScreenProps & {
  baseProduct?: DataCoreBaseProduct | undefined;
}) => {
  const database = useDatabase();

  const { t } = useTranslation("pos");

  const addItem = useAddItem({ navigation });
  const { showMessage } = useFlashMessages();

  const { handleSubmit, clearErrors, watch, trigger, setError, formState } =
    useFormContext<AddCartNonMatchingProductFormValues>();

  const { isLoading, isSubmitting, isValid, isSubmitSuccessful, errors } =
    formState;

  const disabled = isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const handleOnSubmit = React.useCallback(
    async ({ ...input }: AddCartNonMatchingProductFormValues) => {
      // find the internal unknown product

      const unknownProduct = (
        await database
          .get<Product>("products")
          .query(Q.where("stock_code", Q.eq("UNKNOWN_PRODUCT")))
          .fetch()
      )[0];
      if (!unknownProduct) {
        showMessage({
          message: "Could not resolve internal unknown product.",
          severity: "error",
        });
        return;
      }

      await addItem(unknownProduct.id, {
        stockNumber: input.barcode,
        name: input.name,
        basePrice: parseFloat(input.price),
        quantity: parseInt(input.quantity, 10),
        taxRate: parseInt(input.taxRate),
        // costPrice: parseFloat(input.costPrice),
      });

      navigation.pop(2);
    },
    [],
  );

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.add_cart")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  const banner = baseProduct ? <NonMatchingProductBanner /> : undefined;

  return (
    <Page
      title={t("add_non_matched_item_title")}
      secondaryTitle={t("add_non_matched_item_secondary")}
      footer={footerMarkup}
    >
      <Stack vertical>
        {banner}
        <AddCartNonMatchingProductForm.NameFormInput />
        <AddCartNonMatchingProductForm.BarcodeFormInput />
        <AddCartNonMatchingProductForm.PriceFormInput />
        <AddCartNonMatchingProductForm.TaxRateFormInput />
        <AddCartNonMatchingProductForm.QuantityFormInput />
      </Stack>
    </Page>
  );
};

export const AddNonMatchedProductScreen = (
  props: AddNonProductModalScreenProps,
) => {
  const { currentVendor } = useCurrentVendorProvider();
  const { barcode } = props.route?.params;
  const { location } = useActiveLocation();

  const [checkedBaseProduct, setCheckedBaseProduct] =
    React.useState<boolean>(false);

  const [baseProduct, setBaseProduct] = React.useState<
    DataCoreBaseProduct | undefined
  >(undefined);

  const { query } = useQueryFindBaseProductByBarcodeAsync();

  React.useEffect(() => {
    if (!barcode) {
      setCheckedBaseProduct(false);
      return;
    }
    if (
      currentVendor.featureFlags.find(
        (f) =>
          f.code === AccountFeatureFlag.ADMIN_RETAILER_BASE_DATA_LOOKUP_API,
      )?.enabled === false
    ) {
      setCheckedBaseProduct(true);
      return;
    }
    query(barcode)
      .then((baseProduct) => {
        setCheckedBaseProduct(true);
        setBaseProduct(baseProduct);
      })
      .catch(() => {
        setCheckedBaseProduct(false);
      })
      .finally(() => {
        setCheckedBaseProduct(true);
      });
  }, []);

  if (!checkedBaseProduct || !location) {
    return (
      <PlaceholderLoadingScreen
        headerShown={false}
        content={ResourcePageSkeleton}
      />
    );
  }

  return (
    <AddCartNonMatchingProductForm.Provider
      defaultFormValues={{
        ...toDefaultFormValues(baseProduct, location),
        barcode: barcode ?? "",
      }}
    >
      <AddNonMatchedProductContent {...props} baseProduct={baseProduct} />
    </AddCartNonMatchingProductForm.Provider>
  );
};

function toDefaultFormValues(
  baseData?: DataCoreBaseProduct,
  location: Location | null = null,
): AddCartNonMatchingProductFormValues {
  let maybePricing = baseData?.inventoryPricing?.find(
    (p) => p.locationId.toString() === location?.retailerLocationKey,
  );

  if (!maybePricing) {
    maybePricing = baseData?.inventoryPricing?.[0];
  }

  return {
    barcode: baseData?.sku ?? "",
    name: baseData?.stockDescription ?? "",
    quantity: "1",
    price: maybePricing?.stockRetail.toString() ?? "0",
    costPrice: maybePricing?.stockCost.toString() ?? "0",
    taxRate: "0",
  };
}
